import React from 'react'
import { Component } from 'react';
import authService from '../api-authorization/AuthorizeService';
import { QueryParameterNames, LogoutActions, ApplicationPaths, LogoutAction } from '../api-authorization/ApiAuthorizationConstants';
import OverlayProgress from './view/OverlayProgress';
import Main from './layout/Main';
import { Text, PrimaryButton, Stack } from 'office-ui-fabric-react';

interface Props
{
    action: LogoutAction;
}

interface LogoutCallbackResult
{
    status: "logout-callback";
}

interface LogoutResult 
{
    status: "logout";
}

interface LoggedOutResult
{
    status: "logged-out";
}

type LogoutState = LogoutResult | LoggedOutResult | LogoutCallbackResult;

interface State
{
    message?: string;
    isReady: boolean;
    authenticated: boolean;
}

// The main responsibility of this component is to handle the user's logout process.
// This is the starting point for the logout process, which is usually initiated when a
// user clicks on the logout button on the LoginMenu component.
export class Logout extends Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            message: undefined,
            isReady: false,
            authenticated: false
        };
    }

    componentDidMount() {
        const action = this.props.action;
        switch (action) {
            case LogoutActions.Logout:
                if (!!window.history.state.state.local) {
                    this.logout(this.getReturnUrl());
                } else {
                    // This prevents regular links to <app>/authentication/logout from triggering a logout
                    this.setState({ isReady: true, message: "The logout was not initiated from within the page." });
                }
                break;
            case LogoutActions.LogoutCallback:
                this.processLogoutCallback();
                break;
            case LogoutActions.LoggedOut:
                this.setState({ isReady: true, message: "You successfully logged out!" });
                break;
            default:
                throw new Error(`Invalid action '${action}'`);
        }

        this.populateAuthenticationState();
    }

    render() {
        const { isReady, message } = this.state;
        console.log("idle timeout!");    
        if(this.props.action === LogoutActions.LoggedOut)
        {
            return (
                <Main>
                <div style={{padding: "3em"}}>
                    <Stack tokens={{childrenGap: 16}}>
                        <p>
                            <Text variant="xLarge">You have been logged out</Text>
                        </p>
                        <p>
                            <Text variant="medium">
                                To login again please click the bottom below.
                            </Text>
                        </p>
                        <p>
                            <PrimaryButton href="/">Go back to site</PrimaryButton>
                        </p>
                    </Stack>
                </div>
                </Main>
            );
        }

        let label: string | undefined;
        if (!isReady) {
            label = undefined;
        }
        if (!!message) {
            label = message;
        } else {
            const action = this.props.action;
            switch (action) {
                case LogoutActions.Logout:
                    label = "Processing logout";
                    break;
                case LogoutActions.LogoutCallback:
                    label = "Processing logout callback";
                    break;
                default:
                    throw new Error(`Invalid action '${action}'`);
            }
        }

        return <OverlayProgress label={label} />
    }

    async logout(returnUrl: string) {
        const state = { returnUrl };
        const isauthenticated = await authService.isAuthenticated();
        if (isauthenticated) {
            const result = await authService.signOut(state);
            switch (result.status) {
                case "redirect":
                    break;
                case "success":
                    await this.navigateToReturnUrl(returnUrl);
                    break;
                case "fail":
                    this.setState({ message: result.message });
                    break;
                default:
                    throw new Error("Invalid authentication result status.");
            }
        } else {
            this.setState({ message: "You successfully logged out!" });
        }
    }

    async processLogoutCallback() {
        const url = window.location.href;
        const result = await authService.completeSignOut(url);
        switch (result.status) {
            case "redirect":
                // There should not be any redirects as the only time completeAuthentication finishes
                // is when we are doing a redirect sign in flow.
                throw new Error('Should not redirect.');
            case "success":
                await this.navigateToReturnUrl(this.getReturnUrl(result.state));
                break;
            case "fail":
                this.setState({ message: result.message });
                break;
            default:
                throw new Error("Invalid authentication result status.");
        }
    }

    async populateAuthenticationState() {
        const authenticated = await authService.isAuthenticated();
        this.setState({ isReady: true, authenticated });
    }

    getReturnUrl(state?: { returnUrl?: string }) {
        const params = new URLSearchParams(window.location.search);
        const fromQuery = params.get(QueryParameterNames.ReturnUrl);
        if (fromQuery && !fromQuery.startsWith(`${window.location.origin}/`)) {
            // This is an extra check to prevent open redirects.
            throw new Error("Invalid return url. The return url needs to have the same origin as the current page.")
        }
        return (state && state.returnUrl) ||
            fromQuery ||
            `${window.location.origin}${ApplicationPaths.LoggedOut}`;
    }

    navigateToReturnUrl(returnUrl: string) {
        return window.location.replace(returnUrl);
    }
}
